import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ChevronUp, ChevronDown, Trophy } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  calculatePromoterRankLevel,
  getPromotersForRankLevel,
  assignPromotersToRankTiers,
} from "../../utils/rankUtils";
import { IPromoter } from "../../interfaces";
import { PromoterProgressModel, PromoterWithRank } from "../../models";

// Styled components for Rewards section
const RewardsSection = styled.div`
  margin-top: 40px;
`;

const SectionHeaderWithSearch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
`;

const SegmentControl = styled.div`
  display: flex;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const SegmentTab = styled.button<{ active: boolean }>`
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${(props) =>
    props.active
      ? `
    background: #142937;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      color: #1e293b;
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const RewardsFilterDropdown = styled.div`
  position: relative;
`;

const RewardsSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  min-width: 280px;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const RewardGroup = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CollapsibleRewardGroup = styled.div<{ isExpanded: boolean }>`
  margin-bottom: 15px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RewardGroupHeaderClickable = styled.div<{ isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8fafc;
  border-bottom: ${(props) =>
    props.isExpanded ? "1px solid #e2e8f0" : "none"};

  &:hover {
    background: #f1f5f9;
  }
`;

const RewardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RewardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ExpandIcon = styled.div<{ isExpanded: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => (props.isExpanded ? "#3b82f6" : "#e2e8f0")};
  color: ${(props) => (props.isExpanded ? "#ffffff" : "#64748b")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
`;

const RewardGroupNewTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const RewardCount = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const RewardPercentage = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #10b981;
`;

const RewardContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${(props) => (props.isExpanded ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const RewardPromotersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const RewardPromoterCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  min-width: 25%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const RewardPromoterAvatar = styled.div`
  flex-shrink: 0;
`;

const PromoterAvatarSmall = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PromoterAvatarSmallFallback = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RewardPromoterInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RewardPromoterName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const RewardPromoterAmount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #10b981;
`;

const RewardEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const RewardEmptyIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #94a3b8;
`;

const RewardEmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const RewardEmptyText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

interface FundraiserPromotersTabRewardsProps {
  promoters: PromoterProgressModel[];
}

export const FundraiserPromotersTabRewards: React.FC<FundraiserPromotersTabRewardsProps> =
  observer(({ promoters }) => {
    const [activeTab, setActiveTab] = useState<"level" | "rank">("level");
    const [rewardFilter, setRewardFilter] = useState("all");
    const [rewardImageErrors, setRewardImageErrors] = useState<Set<string>>(
      new Set()
    );
    const [expandedRewards, setExpandedRewards] = useState<Set<string>>(
      new Set()
    );
    const [expandedRanks, setExpandedRanks] = useState<Set<string>>(new Set());

    const selectedFundraiser = fundraiserStore.selectedFundraiser;

    // Load builder data to ensure localStorage rewards/ranks are loaded
    React.useEffect(() => {
      if (selectedFundraiser?.id) {
        // Always reload builder data to ensure localStorage data is fresh
        fundraiserStore.initializeBuilder(selectedFundraiser);
      }
    }, [selectedFundraiser?.id]);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    };

    const handleRewardImageError = (promoterId: string) => {
      setRewardImageErrors((prev) => {
        const next = new Set(prev);
        next.add(promoterId);
        return next;
      });
    };

    const getFilteredRewards = () => {
      if (!uniqueRewards) return [];

      if (rewardFilter === "all") {
        return uniqueRewards.slice().sort((a, b) => b.level - a.level); // Highest to lowest
      } else {
        return uniqueRewards.filter((a) => a.id === rewardFilter);
      }
    };

    const getPromotersForReward = (rewardLevel: number) => {
      return promoters.filter(
        (promoter) => promoter.totalRaisedAmount >= rewardLevel
      );
    };

    const getRewardProgress = (rewardLevel: number) => {
      const qualifiedPromoters = getPromotersForReward(rewardLevel);
      if (promoters.length === 0) return 0;
      return (qualifiedPromoters.length / promoters.length) * 100;
    };

    const toggleRewardExpansion = (rewardId: string) => {
      const newExpanded = new Set(expandedRewards);
      if (newExpanded.has(rewardId)) {
        newExpanded.delete(rewardId);
      } else {
        newExpanded.add(rewardId);
      }
      setExpandedRewards(newExpanded);
    };

    const toggleRankExpansion = (rankId: string) => {
      const newExpanded = new Set(expandedRanks);
      if (newExpanded.has(rankId)) {
        newExpanded.delete(rankId);
      } else {
        newExpanded.add(rankId);
      }
      setExpandedRanks(newExpanded);
    };

    // Rank-based reward logic
    const getPromotersWithRanks = () => {
      return promoters
        .slice()
        .sort((a, b) => b.totalRaisedAmount - a.totalRaisedAmount)
        .map(
          (promoter, index) =>
            new PromoterWithRank({
              ...promoter,
              currentRank: promoter.rank || index + 1,
              actualRank: promoter.rank || index + 1,
              name: promoter.displayName,
              totalRaised: promoter.totalRaisedAmount,
            })
        );
    };

    const getPromotersForRank = (rankDefinition: string) => {
      // For rank-based rewards, promoters should only appear in their highest achieved tier
      const promotersWithRanks = getPromotersWithRanks();
      const assignments = assignPromotersToRankTiers(
        promotersWithRanks,
        uniqueRanks
      );

      // Find promoters whose highest achieved rank tier matches this specific tier
      return assignments
        .filter((assignment) => {
          if (rankDefinition === "no-reward" || rankDefinition.includes("+")) {
            // For "No Reward" tier, return promoters with no rank assignment
            return assignment.rankLevel === null;
          }
          // For regular rank tiers, return promoters assigned to this specific tier
          return assignment.rankLevel?.rank === rankDefinition;
        })
        .map((assignment) => assignment.promoter);
    };

    const getRankProgress = (rankDefinition: string) => {
      const qualifiedPromoters = getPromotersForRank(rankDefinition);
      if (promoters.length === 0) return 0;
      return (qualifiedPromoters.length / promoters.length) * 100;
    };

    const renderRewardPromoterAvatar = (promoter: PromoterProgressModel) => {
      if (rewardImageErrors.has(promoter.id)) {
        return (
          <PromoterAvatarSmallFallback>
            {getInitials(promoter.displayName)}
          </PromoterAvatarSmallFallback>
        );
      }

      return (
        <PromoterAvatarSmall
          src={promoter.imageURL}
          alt={promoter.displayName}
          onError={() => handleRewardImageError(promoter.id)}
        />
      );
    };

    // Check if we have any rewards to show
    // Get rewards and ranks from both fundraiser data and builder data (localStorage)
    const allRewards = [
      ...(selectedFundraiser?.rewards || []),
      ...(fundraiserStore.builderData?.rewards || []),
    ];
    const allRanks = [
      ...(selectedFundraiser?.ranks || []),
      ...(fundraiserStore.builderData?.ranks || []),
    ];

    // Remove duplicates by id
    const uniqueRewards = allRewards.filter(
      (reward, index, arr) => arr.findIndex((a) => a.id === reward.id) === index
    );
    const uniqueRanks = allRanks.filter(
      (rank, index, arr) => arr.findIndex((r) => r.id === rank.id) === index
    );

    const hasLevelRewards = uniqueRewards.length > 0;
    const hasRankRewards = uniqueRanks.length > 0;
    const hasBothRewardTypes = hasLevelRewards && hasRankRewards;

    // Don't render if no rewards or ranks exist
    if (!hasLevelRewards && !hasRankRewards) {
      return null;
    }

    // If only one type exists, default to that type
    React.useEffect(() => {
      if (!hasBothRewardTypes) {
        if (hasLevelRewards && !hasRankRewards) {
          setActiveTab("level");
        } else if (hasRankRewards && !hasLevelRewards) {
          setActiveTab("rank");
        }
      }
    }, [hasLevelRewards, hasRankRewards, hasBothRewardTypes]);

    const renderLevelRewards = () => (
      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        {uniqueRewards
          .slice()
          .sort((a, b) => b.level - a.level)
          .map((reward) => (
            <CollapsibleRewardGroup
              key={reward.id}
              isExpanded={expandedRewards.has(reward.id)}
            >
              <RewardGroupHeaderClickable
                isExpanded={expandedRewards.has(reward.id)}
                onClick={() => toggleRewardExpansion(reward.id)}
              >
                <RewardHeaderLeft>
                  <RewardGroupNewTitle>
                    {reward.reward}{" "}
                    <RewardCount>
                      ({getPromotersForReward(reward.level).length})
                    </RewardCount>
                  </RewardGroupNewTitle>
                </RewardHeaderLeft>
                <RewardHeaderRight>
                  <div style={{ textAlign: "right" }}>
                    <RewardPercentage>
                      <span style={{ color: "#10b981" }}>
                        {getRewardProgress(reward.level).toFixed(0)}% achieved
                      </span>
                      <span style={{ color: "#555555", marginLeft: "10px" }}>
                        {formatCurrency(reward.level)}
                      </span>
                    </RewardPercentage>
                  </div>
                  <ExpandIcon
                    isExpanded={expandedRewards.has(reward.id)}
                    style={{ width: "32px", height: "32px" }}
                  >
                    {expandedRewards.has(reward.id) ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </ExpandIcon>
                </RewardHeaderRight>
              </RewardGroupHeaderClickable>

              <RewardContent isExpanded={expandedRewards.has(reward.id)}>
                <div style={{ padding: "24px" }}>
                  <RewardPromotersGrid>
                    {getPromotersForReward(reward.level).map((promoter) => (
                      <RewardPromoterCard key={promoter.id}>
                        <RewardPromoterAvatar>
                          {renderRewardPromoterAvatar(promoter)}
                        </RewardPromoterAvatar>
                        <RewardPromoterInfo>
                          <RewardPromoterName>
                            {promoter.displayName}
                          </RewardPromoterName>
                          <RewardPromoterAmount>
                            {formatCurrency(promoter.totalRaisedAmount)}
                          </RewardPromoterAmount>
                        </RewardPromoterInfo>
                      </RewardPromoterCard>
                    ))}
                  </RewardPromotersGrid>

                  {getPromotersForReward(reward.level).length === 0 && (
                    <RewardEmptyState>
                      <RewardEmptyIcon>
                        <Trophy size={32} />
                      </RewardEmptyIcon>
                      <RewardEmptyTitle>Level Not Achieved</RewardEmptyTitle>
                      <RewardEmptyText>
                        No promoters have reached the{" "}
                        {formatCurrency(reward.level)} level yet.
                      </RewardEmptyText>
                    </RewardEmptyState>
                  )}
                </div>
              </RewardContent>
            </CollapsibleRewardGroup>
          ))}
      </div>
    );

    const renderRankRewards = () => {
      const sortedRanks = uniqueRanks.slice().sort((a, b) => {
        const aRank =
          typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
        const bRank =
          typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
        return (aRank || 0) - (bRank || 0);
      });

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {sortedRanks.map((rank) => (
            <CollapsibleRewardGroup
              key={rank.id}
              isExpanded={expandedRanks.has(rank.id)}
            >
              <RewardGroupHeaderClickable
                isExpanded={expandedRanks.has(rank.id)}
                onClick={() => toggleRankExpansion(rank.id)}
              >
                <RewardHeaderLeft>
                  <RewardGroupNewTitle>
                    {rank.reward}{" "}
                    <RewardCount>
                      ({getPromotersForRank(rank.rank).length})
                    </RewardCount>
                  </RewardGroupNewTitle>
                </RewardHeaderLeft>
                <RewardHeaderRight>
                  <div style={{ textAlign: "right" }}>
                    <RewardPercentage>
                      <span style={{ color: "#10b981" }}>
                        {getRankProgress(rank.rank).toFixed(0)}% achieved
                      </span>
                      <span style={{ color: "#555555", marginLeft: "10px" }}>
                        Rank {rank.rank}
                      </span>
                    </RewardPercentage>
                  </div>
                  <ExpandIcon
                    isExpanded={expandedRanks.has(rank.id)}
                    style={{ width: "32px", height: "32px" }}
                  >
                    {expandedRanks.has(rank.id) ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </ExpandIcon>
                </RewardHeaderRight>
              </RewardGroupHeaderClickable>

              <RewardContent isExpanded={expandedRanks.has(rank.id)}>
                <div style={{ padding: "24px" }}>
                  <RewardPromotersGrid>
                    {getPromotersForRank(rank.rank).map((promoter) => {
                      const promotersWithRanks = getPromotersWithRanks();
                      const { effectiveRank } = calculatePromoterRankLevel(
                        promoter,
                        promotersWithRanks,
                        uniqueRanks
                      );
                      return (
                        <RewardPromoterCard key={promoter.id}>
                          {/* <RewardPromoterAvatar>
                            {renderRewardPromoterAvatar(promoter)}
                          </RewardPromoterAvatar> */}
                          <RewardPromoterInfo>
                            <RewardPromoterName>
                              {promoter.name}
                            </RewardPromoterName>
                            <RewardPromoterAmount>
                              {formatCurrency(promoter.totalRaised)}
                            </RewardPromoterAmount>
                          </RewardPromoterInfo>
                        </RewardPromoterCard>
                      );
                    })}
                  </RewardPromotersGrid>

                  {getPromotersForRank(rank.rank).length === 0 && (
                    <RewardEmptyState>
                      <RewardEmptyIcon>
                        <Trophy size={32} />
                      </RewardEmptyIcon>
                      <RewardEmptyTitle>Level Not Achieved</RewardEmptyTitle>
                      <RewardEmptyText>
                        No promoters have reached this level yet.
                        {rank.minimum !== 0 && rank.minimum && rank.minimum > 0
                          ? ` ${formatCurrency(rank.minimum)} minimum donations required.`
                          : ""}
                      </RewardEmptyText>
                    </RewardEmptyState>
                  )}

                  {/* Show minimum donation requirement if it exists and promoters are shown */}
                  {rank.minimum !== 0 &&
                    rank.minimum &&
                    rank.minimum > 0 &&
                    getPromotersForRank(rank.rank).length > 0 && (
                      <div
                        style={{
                          marginTop: "16px",
                          fontSize: "14px",
                          color: "#64748b",
                          fontStyle: "normal",
                          textAlign: "left",
                        }}
                      >
                        {formatCurrency(rank.minimum)} minimum donations
                        required.
                      </div>
                    )}
                </div>
              </RewardContent>
            </CollapsibleRewardGroup>
          ))}
        </div>
      );
    };

    return (
      <RewardsSection>
        <SectionHeaderWithSearch>
          <SectionTitle>Rewards</SectionTitle>

          {hasBothRewardTypes && (
            <HeaderCenter>
              <SegmentControl>
                <SegmentTab
                  active={activeTab === "level"}
                  onClick={() => setActiveTab("level")}
                >
                  By Amount
                </SegmentTab>
                <SegmentTab
                  active={activeTab === "rank"}
                  onClick={() => setActiveTab("rank")}
                >
                  By Rank
                </SegmentTab>
              </SegmentControl>
            </HeaderCenter>
          )}
        </SectionHeaderWithSearch>

        {activeTab === "level" && hasLevelRewards && renderLevelRewards()}
        {activeTab === "rank" && hasRankRewards && renderRankRewards()}
      </RewardsSection>
    );
  });
